// AppClose "Records Export" PDF ingestion adapter — browser/pdf.js twin of
// evals/adapters/appclose.py. Format analysis:
// docs/topics/ingestion/appclose-format.md.
//
// Parses by COLUMN GEOMETRY, not by line regex. Record headers wrap
// unpredictably and a wrapped fragment can itself look like a valid record
// start ("Alex" / "Rivera on July 1, 2024 at 6:52 AM texted (…):"), which
// misattributed 112 senders on the first real corpus. The rule: accumulate
// consecutive header-column lines and stop only when the accumulation PARSES
// as a complete record, with balanced parens.
//
// ---------------------------------------------------------------------------
// WHY THE COLUMNS HERE DIFFER FROM THE PYTHON ONES
// ---------------------------------------------------------------------------
// This is not a transcription of appclose.py's constants, and it must not be
// "corrected" into one. pdfplumber reports each glyph's inked bounding box;
// pdf.js reports the text-space pen position. They differ by the font's left
// side bearing — on the real AppClose corpus the record-header column reads
// 48.04 under pdfplumber and 48.52 under pdf.js. 48.52 is the SECTION-header
// column, so a straight port classifies every record header as a date header
// and yields zero records.
//
// pdf.js also groups the ↪ marker onto the SAME row as the quoted header it
// introduces, where pdfplumber puts the marker on its own row. So the quote
// block's opening row here carries both, and is split on the marker.
//
// Both divergences are invisible against a synthetic fixture built with a
// base-14 font (no left side bearing, no ↪ glyph), which is exactly how the
// 2houses right-aligned-timestamp bug shipped green. The numbers below were
// measured by running the REAL export through this file's own extractor.

import { extractPageLines, type PositionedLine } from "./_pdf";

export const PARSER_VERSION = "0.1.0";
export const ADAPTER_NAME = "appclose";

/** Column layout as pdf.js reports it. See the note above before editing. */
export const APPCLOSE_COLUMNS = {
  furniture: 42.52,
  /** Section title (height >= 20) and date header (height ~12). */
  section: 48.52,
  /** Record header + wrapped continuations. Shares `section`'s x under
   *  pdf.js — height is what separates them (~10.2 vs 12). */
  record: 48.52,
  body: 71.02,
  attachRef: 80.01,
  /** Row that opens a quoted-reply block; carries the quoted header too. */
  quoteMark: 57.52,
  quoteBody: 72.52,
  quoteHeader: 73.27,
} as const;

/** Column-identity tolerance: body 71.02 vs quoted body 72.52 are 1.5 pt
 *  apart, so anything >= 1.5 merges quoted text into real messages. */
export const TOL = 1.0;

/** A record header is height ~10.2; a date header at the same x is 12. */
export const SECTION_MIN_HEIGHT = 11.5;

export const X_PAGE_NUMBER_MIN = 200;
const PAGE_NUM_RE = /^Page \d+ of \d+$/;

const DATE = String.raw`[A-Z][a-z]+ \d{1,2}, \d{4}`;
const TIME = String.raw`\d{1,2}:\d{2} [AP]M`;

// pdf.js renders " : " with spacing pdfplumber collapses, so every literal
// colon here tolerates surrounding whitespace.
const VIEWED = String.raw`(?:\s*\(viewed by (.+?) on (${DATE}) at (${TIME})\))?`;
export const RECORD_RE = new RegExp(
  String.raw`^(.+?) on (${DATE}) at (${TIME}) (.+?)${VIEWED}\s*:\s*$`,
);
export const EVENT_RE = new RegExp(
  String.raw`^(.+?) on (${DATE}) at (${TIME}) ((?:Incoming|Outgoing|Received|Requested).*)$`,
);
export const ATTACH_REF_RE = /^\(See attached\s+on page (\d+)\)$/;
export const ORIG_REF_RE = /^\(See original message\s+on page (\d+)\)$/;
export const ATTACH_NAME_RE = /^(?:Img\.|File)\s+(.+?)\s+to page (\d+)$/;

const PERIOD_RE = new RegExp(
  String.raw`Period\s*:\s*(${DATE} at ${TIME}) to (${DATE} at ${TIME})`,
);
const REQUESTED_BY_RE = new RegExp(
  String.raw`Requested by\s*:\s*(.+?)\s*\(([^)]*)\), signed up on (${DATE} at ${TIME})`,
);

// Cairo writes some emoji into the text layer as their raw codepoint spelling
// ("She is out 01F01F"). Left in, that puts a token in the evidence nobody
// typed; mapped back to a character, we would be inventing evidence off a
// guess about glyph naming. Stripped and counted. Narrow on purpose: the
// token must carry the supplementary-plane "1F" marker, so a bare number
// someone actually typed ("937") survives.
const GLYPH_ARTIFACT_RE = /\s*\b0?1F[0-9A-F]{2,5}\b/g;

// The same artifact, but as a WHOLE ROW. pdfplumber fuses cairo's codepoint
// spelling into the body run it sits in ("She is out 01F01F"), where the token
// regex above strips it. pdf.js keeps it as its own text item, so it arrives as
// a separate row AT the body column (x0 71.57, height 4.55 against the body's
// 71.02/10.00 — inside TOL) and would be appended to the message as "01F":
// text the sender never typed, written into evidence.
//
// Requiring the leading 0 keeps this off real content. "01F" as an entire
// message body is implausible; "1F" alone (an apartment number) is not, and
// does not match. A row of digits someone actually typed ("2024") does not
// match either — the 1F marker is mandatory.
const GLYPH_ARTIFACT_ROW_RE = /^(?:01F[0-9A-F]*\s*)+$/;

// AppClose's own UI placeholder, rendered at the BODY column. Exact full-line
// match only — a message quoting this sentence must survive.
const BOILERPLATE = new Set([
  "*This screen will display messages that will appear here once you send or receive a message.",
]);

const MONTHS: Record<string, number> = {
  January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
  July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
};

export class MalformedAppCloseError extends Error {}

export interface CanonicalRow {
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  attachments: string;
}

export interface AttachmentRef {
  page: number;
  filename: string | null;
}

export interface ReplyTo {
  header: string;
  body: string;
  orig_page: number;
}

export interface SourceMetadata {
  source: string;
  message_number: number;
  kind: "message" | "attachment";
  predicate: string;
  viewed_by: string | null;
  viewed_at: string | null;
  attachments_structured: AttachmentRef[];
  reply_to: ReplyTo | null;
  source_page: number;
}

export interface CallEvent {
  timestamp: string;
  sender: string;
  predicate: string;
  source_page: number;
}

export interface RecordInfo {
  period_start: string;
  period_end: string;
  requested_by: string;
  requested_by_email: string;
  signed_up: string;
}

export interface IngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  glyph_artifacts_stripped: number;
  date_range: { start: string | null; end: string | null };
  timezone: string | null;
  /** AppClose declares no zone anywhere — true until the user assigns one. */
  needs_timezone: boolean;
  zone_source: string | null;
  volume_by_party: Record<string, number>;
  attachment_count: number;
  reply_count: number;
  read_receipt_count: number;
  event_count: number;
  quote_blocks_folded: number;
  unparsed_headers: number;
  unknown_column_lines: number;
  decorative_glyph_lines: number;
  pending_message_count?: number;
  page_count?: number;
}

export interface CaseMeta {
  id: string;
  privacy_class: string;
  prompt_vars: {
    party_a: string;
    party_b: string;
    format: string;
    time_period: string;
    context: string;
  };
  known_patterns: string[];
  notes: string;
  source_timezone?: string;
  predicted_timezone?: string;
  user_timezone?: string;
}

export interface IngestResult {
  rows: CanonicalRow[];
  source_metas: SourceMetadata[];
  events: CallEvent[];
  stats: IngestStats;
  csv: string;
  case_meta: CaseMeta;
  record_info: RecordInfo;
  warnings: string[];
}

interface NaiveDt {
  year: number; month: number; day: number; hour: number; minute: number;
}

interface RawRecord {
  page: number;
  sectionDate: string;
  header: string;
  body: string[];
  refs: string[];
  replyTo: ReplyTo | null;
  complete: boolean;
}

export interface ParsedRecord {
  source_page: number;
  sender: string;
  dt: NaiveDt;
  kind: "message" | "attachment" | "event";
  predicate: string;
  body: string;
  viewed_by: string | null;
  viewed_dt: NaiveDt | null;
  attachments: AttachmentRef[];
  reply_to: ReplyTo | null;
}

export interface ParseCounts {
  quote_blocks: number;
  unknown_column_lines: number;
  unknown_column_samples: string[];
  decorative_glyph_lines: number;
  /** Rows at the body column that are wholly a cairo codepoint spelling. */
  glyph_artifact_rows: number;
}

const near = (x: number, col: number, tol = TOL) => Math.abs(x - col) < tol;

function balanced(s: string): boolean {
  let n = 0;
  for (const ch of s) {
    if (ch === "(") n++;
    else if (ch === ")") n--;
  }
  return n === 0;
}

/** Shape-only: date VALIDITY is checked at emit time, so a header-shaped line
 *  with an impossible date fails loudly instead of making the accumulator run
 *  away and swallow the rest of the document. */
export function headerComplete(text: string): RegExpMatchArray | null {
  if (!balanced(text)) return null;
  return text.match(RECORD_RE) ?? text.match(EVENT_RE);
}

export function parseDt(dateS: string, timeS: string): NaiveDt | null {
  const dm = dateS.match(/^([A-Z][a-z]+) (\d{1,2}), (\d{4})$/);
  const tm = timeS.match(/^(\d{1,2}):(\d{2}) ([AP])M$/);
  if (!dm || !tm) return null;
  const month = MONTHS[dm[1]];
  if (!month) return null;
  const day = Number(dm[2]);
  const year = Number(dm[3]);
  let hour = Number(tm[1]);
  const minute = Number(tm[2]);
  if (hour < 1 || hour > 12 || minute > 59) return null;
  if (tm[3] === "P" && hour !== 12) hour += 12;
  if (tm[3] === "A" && hour === 12) hour = 0;
  // Reject impossible calendar dates (February 30) the same way Python's
  // strptime does — a look-alike is not a record.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return { year, month, day, hour, minute };
}

/** Localize a naive wall clock to `tz` and render UTC ISO-8601 'Z'.
 *
 *  The export prints no offset, so during the autumn fall-back the instant is
 *  genuinely unknowable; this resolves to the earlier (pre-transition) offset,
 *  matching the Python side's fold=0. */
export function toUtcIso(dt: NaiveDt, tz: string): string {
  const guess = Date.UTC(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute);
  // Two passes converge for every real zone offset.
  let ms = guess;
  for (let i = 0; i < 2; i++) {
    const offset = zoneOffsetMs(ms, tz);
    ms = guess - offset;
  }
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

function zoneOffsetMs(utcMs: number, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  const asUtc = Date.UTC(
    get("year"), get("month") - 1, get("day"),
    get("hour") % 24, get("minute"), get("second"),
  );
  return asUtc - utcMs;
}

export function stripGlyphArtifacts(text: string): { text: string; count: number } {
  if (!text) return { text, count: 0 };
  const matches = text.match(GLYPH_ARTIFACT_RE);
  if (!matches) return { text, count: 0 };
  return { text: text.replace(GLYPH_ARTIFACT_RE, "").trim(), count: matches.length };
}

/** A line that is nothing but rendering artifacts — the Private-Use-Area icon
 *  AppClose draws beside every ↪, and cairo's stray codepoint fragments.
 *  Classifying them keeps `unknown_column_lines` usable as an alarm; a counter
 *  that is never zero is not an alarm. */
export function isDecorativeGlyph(text: string): boolean {
  const s = text.trim();
  if (!s) return false;
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    const pua = c >= 0xe000 && c <= 0xf8ff;
    if (!pua && !/\s/.test(ch) && !"0123456789ABCDEF".includes(ch)) return false;
  }
  return true;
}

function isFurniture(x0: number, text: string): boolean {
  if (near(x0, APPCLOSE_COLUMNS.furniture)) return true;
  return x0 >= X_PAGE_NUMBER_MIN && PAGE_NUM_RE.test(text);
}

/**
 * Walk positioned lines and accumulate records.
 *
 * Takes positioned lines rather than a PDF so the rules can be exercised
 * against exact synthetic geometry — the only way to test the ↪ path and
 * emoji bodies, since no font a fixture generator can rely on encodes either.
 */
export function parsePositionedLines(
  pages: { pageNumber: number; lines: PositionedLine[] }[],
): { records: RawRecord[]; info: RecordInfo; counts: ParseCounts } {
  const records: RawRecord[] = [];
  const info: RecordInfo = {
    period_start: "", period_end: "", requested_by: "",
    requested_by_email: "", signed_up: "",
  };
  const counts: ParseCounts = {
    quote_blocks: 0, unknown_column_lines: 0,
    unknown_column_samples: [], decorative_glyph_lines: 0,
    glyph_artifact_rows: 0,
  };
  let cur: RawRecord | null = null;
  let sectionDate = "";
  let inQuote = false;
  let quote: { hdr: string; body: string[] } | null = null;

  for (const { pageNumber, lines } of pages) {
    const furniture: string[] = [];
    for (const { x0, height, text } of lines) {
      if (!text) continue;

      if (isFurniture(x0, text)) {
        furniture.push(text);
        continue;
      }

      // Section title / date header. Under pdf.js this shares the record
      // column, so HEIGHT is the discriminator, not x.
      if (near(x0, APPCLOSE_COLUMNS.section) && height >= SECTION_MIN_HEIGHT) {
        if (height < 20) sectionDate = text;
        if (cur) { records.push(cur); cur = null; }
        inQuote = false; quote = null;
        continue;
      }

      // Quote block opener. pdf.js puts the ↪ and the quoted header on ONE
      // row, so the row is split on the marker and the remainder seeds the
      // quoted header (pdfplumber gives them as two rows; both shapes work).
      if (text.startsWith("↪") || near(x0, APPCLOSE_COLUMNS.quoteMark)) {
        const rest = text.replace(/^↪\s*/, "").trim();
        inQuote = true;
        quote = { hdr: rest, body: [] };
        counts.quote_blocks++;
        continue;
      }

      if (inQuote && quote) {
        const m = text.match(ORIG_REF_RE);
        if (m) {
          // The block's exact back-pointer closes it. Attach to the open
          // record, or to the last one emitted when the reply sat at the foot
          // of a page and its block continued past the repeated date header.
          const target = cur ?? (records.length ? records[records.length - 1] : null);
          if (target) {
            target.replyTo = {
              header: quote.hdr.trim(),
              body: quote.body.join(" ").trim(),
              orig_page: Number(m[1]),
            };
          }
          inQuote = false; quote = null;
        } else if (!headerComplete(quote.hdr)) {
          quote.hdr = `${quote.hdr} ${text}`.trim();
        } else {
          quote.body.push(text);
        }
        continue;
      }

      // Record header + wrapped continuations.
      if (near(x0, APPCLOSE_COLUMNS.record)) {
        if (!cur || cur.complete) {
          if (cur) records.push(cur);
          cur = {
            page: pageNumber, sectionDate, header: text,
            body: [], refs: [], replyTo: null, complete: false,
          };
        } else {
          cur.header = `${cur.header} ${text}`.trim();
        }
        if (headerComplete(cur.header)) cur.complete = true;
        continue;
      }

      if (near(x0, APPCLOSE_COLUMNS.attachRef)) {
        if (cur) { cur.refs.push(text); cur.complete = true; }
        continue;
      }

      if (near(x0, APPCLOSE_COLUMNS.body)) {
        if (BOILERPLATE.has(text)) { counts.decorative_glyph_lines++; continue; }
        if (GLYPH_ARTIFACT_ROW_RE.test(text)) { counts.glyph_artifact_rows++; continue; }
        if (cur) { cur.body.push(text); cur.complete = true; }
        continue;
      }

      if (isDecorativeGlyph(text)) { counts.decorative_glyph_lines++; continue; }

      counts.unknown_column_lines++;
      if (counts.unknown_column_samples.length < 10) {
        counts.unknown_column_samples.push(`x0=${x0.toFixed(2)} ${text.slice(0, 80)}`);
      }
    }

    if (furniture.length && !info.period_start) {
      const joined = furniture.join(" ");
      const pm = joined.match(PERIOD_RE);
      if (pm) { info.period_start = pm[1]; info.period_end = pm[2]; }
      const rm = joined.match(REQUESTED_BY_RE);
      if (rm) {
        info.requested_by = rm[1].trim();
        info.requested_by_email = rm[2].trim();
        info.signed_up = rm[3];
      }
    }
  }
  if (cur) records.push(cur);
  return { records, info, counts };
}

function kindFor(predicate: string): ParsedRecord["kind"] {
  if (predicate.startsWith("sent attachment")) return "attachment";
  if (predicate.startsWith("texted")) return "message";
  return "event";
}

export function shapeRecords(
  raws: RawRecord[],
  names: Map<number, string>,
): { records: ParsedRecord[]; unparsed: string[] } {
  const records: ParsedRecord[] = [];
  const unparsed: string[] = [];
  for (const r of raws) {
    const m = headerComplete(r.header);
    if (!m) { unparsed.push(r.header); continue; }
    const isRecord = RECORD_RE.test(r.header);
    const sender = m[1].trim();
    const dt = parseDt(m[2], m[3]);
    if (!dt) { unparsed.push(r.header); continue; }
    const predicate = (m[4] ?? "").trim();
    const viewedBy = isRecord ? (m[5] ?? null) : null;
    const viewedDt = isRecord && m[6] && m[7] ? parseDt(m[6], m[7]) : null;

    const attachments: AttachmentRef[] = [];
    for (const ref of r.refs) {
      const rm = ref.match(ATTACH_REF_RE);
      if (rm) {
        const page = Number(rm[1]);
        attachments.push({ page, filename: names.get(page) ?? null });
      }
    }
    records.push({
      source_page: r.page,
      sender,
      dt,
      kind: kindFor(predicate),
      predicate,
      body: r.body.join(" ").trim(),
      viewed_by: viewedBy ? viewedBy.trim() : null,
      viewed_dt: viewedDt,
      attachments,
      reply_to: r.replyTo,
    });
  }
  return { records, unparsed };
}

/** Attachment-zone captions: "Img. <name> to page <origin>". Message-side
 *  refs carry only a page number, so filenames resolve ONLY by cross-
 *  referencing the two zones. */
/**
 * Index of the first attachment-zone page, i.e. the count of message-log pages.
 *
 * The export has two zones and only the first holds messages. appclose.py finds
 * the boundary via `page.images` (the attachment zone renders exactly one image
 * per page, the message log none), which pdf.js does not expose without pulling
 * each page's operator list. The zone's OWN caption is an equally exact signal
 * and costs nothing: "Img. <name> to page <origin>" appears on attachment pages
 * and nowhere in the message log.
 *
 * Feeding the whole document to the parser instead — which this adapter used to
 * do — put all 1,474 attachment captions at an unmodelled column, so
 * `unknown_column_lines` read 1,462 against pdfplumber's 0. No records were
 * invented, but a counter that is never zero cannot serve as the alarm it was
 * built to be, and it masks the real layout variants it exists to catch.
 */
export function findMessageZone(
  pages: { pageNumber: number; lines: PositionedLine[] }[],
): number {
  for (let i = 0; i < pages.length; i++) {
    if (pages[i].lines.some((l) => ATTACH_NAME_RE.test(l.text))) return i;
  }
  return pages.length;
}

/**
 * Map attachment-zone page number → filename.
 *
 * Message-side refs carry only a page number; the filename lives on that page.
 * Scanned from the zone boundary on, never over the message log, so a message
 * that happens to read like a caption cannot invent an attachment name.
 */
export function resolveAttachmentNames(
  pages: { pageNumber: number; lines: PositionedLine[] }[],
  zoneEnd = 0,
): Map<number, string> {
  const names = new Map<number, string>();
  for (const { pageNumber, lines } of pages.slice(zoneEnd)) {
    for (const { text } of lines) {
      const m = text.match(ATTACH_NAME_RE);
      if (m) names.set(pageNumber, m[1].trim());
    }
  }
  return names;
}

const C0_C1 = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g;
function sanitize(s: string): { text: string; stripped: number } {
  const hits = s.match(C0_C1);
  return { text: s.replace(C0_C1, ""), stripped: hits ? hits.length : 0 };
}

function determineParties(records: ParsedRecord[]): {
  party_a: string; party_b: string; out_of_set: string[];
} {
  const counts = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  records.forEach((r, i) => {
    counts.set(r.sender, (counts.get(r.sender) ?? 0) + 1);
    if (!firstSeen.has(r.sender)) firstSeen.set(r.sender, i);
  });
  // A recipient who never wrote is still a party.
  records.forEach((r, i) => {
    if (r.viewed_by) {
      if (!counts.has(r.viewed_by)) counts.set(r.viewed_by, 0);
      if (!firstSeen.has(r.viewed_by)) firstSeen.set(r.viewed_by, records.length + i);
    }
  });
  const ordered = [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || (firstSeen.get(a[0]) ?? 0) - (firstSeen.get(b[0]) ?? 0),
  );
  const party_a = ordered[0]?.[0] ?? "";
  const party_b = ordered[1]?.[0] ?? "";
  const declared = new Set([party_a, party_b].filter(Boolean));
  const out_of_set = [...new Set(records.map((r) => r.sender))]
    .filter((s) => !declared.has(s))
    .sort();
  return { party_a, party_b, out_of_set };
}

function attachmentsSummary(items: AttachmentRef[]): string {
  if (!items.length) return "";
  const names = items
    .map((i) => i.filename ?? `(unnamed, page ${i.page})`)
    .join(", ");
  return `${items.length} attachment${items.length === 1 ? "" : "s"}: ${names}`;
}

function csvCell(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function rowsToCsv(rows: CanonicalRow[]): string {
  const head = "timestamp,sender,recipient,subject,body,attachments\n";
  return (
    head +
    rows
      .map((r) =>
        [r.timestamp, r.sender, r.recipient, r.subject, r.body, r.attachments]
          .map(csvCell)
          .join(","),
      )
      .join("\n") +
    (rows.length ? "\n" : "")
  );
}

function formatTimePeriod(start: string | null, end: string | null): string {
  if (!start || !end) return "";
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} ${d.getUTCFullYear()}`;
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Parse an AppClose export from already-extracted positioned pages.
 *
 * `timezone` is REQUIRED to place the export on a timeline — it declares no
 * offset anywhere, so guessing would skew every timestamp in a corpus whose
 * time-of-day pattern is itself evidence. Without one the parse still runs
 * (so the counts and the format check are honest) but every record is held,
 * `needs_timezone` is true, and the caller routes to the zone prompt.
 */
/**
 * Unknown-column lines allowed before we refuse the file outright.
 *
 * `unknown_column_lines` was built as an alarm and was only ever a warning
 * string in the done summary. On the real export the browser engine hit 1,462
 * of them (pdfplumber: 0) and committed a corpus missing 43% of its messages,
 * every attachment and every reply link — with the right parties and the right
 * date range on screen, so nothing looked wrong.
 *
 * A parse that cannot place a quarter of its lines has not understood the
 * layout, and a wrong corpus is worse than no corpus: refusing routes to the
 * wizard's "we couldn't read this" recovery, which is an honest handoff.
 * The floor keeps a handful of stray decorative rows from blocking a file that
 * parsed fine.
 */
export const UNKNOWN_COLUMN_FLOOR = 25;
export const UNKNOWN_COLUMN_MAX_RATIO = 0.02;

export function assertColumnsRecognized(unknown: number, records: number): void {
  if (unknown > UNKNOWN_COLUMN_FLOOR && unknown > records * UNKNOWN_COLUMN_MAX_RATIO) {
    throw new MalformedAppCloseError(
      `${unknown} line(s) sat at an unrecognized column against ${records} ` +
        `record(s) — this export's layout is not one this reader models, and ` +
        `parsing it further would drop or misattribute messages.`,
    );
  }
}

export function ingestAppCloseFromPages(
  pages: { pageNumber: number; lines: PositionedLine[] }[],
  opts: { corpusId: string; timezone?: string | null },
): IngestResult {
  if (pages.length === 0) {
    throw new MalformedAppCloseError("PDF contained no extractable text");
  }
  const zoneEnd = findMessageZone(pages);
  const { records: raws, info, counts } = parsePositionedLines(pages.slice(0, zoneEnd));
  const names = resolveAttachmentNames(pages, zoneEnd);
  const { records, unparsed } = shapeRecords(raws, names);
  if (records.length === 0) {
    throw new MalformedAppCloseError(
      "No AppClose records detected — likely wrong PDF format.",
    );
  }
  assertColumnsRecognized(counts.unknown_column_lines, records.length);

  const tz = opts.timezone || null;
  const messageLike = records.filter((r) => r.kind !== "event");
  const eventCount = records.length - messageLike.length;

  if (!tz) {
    const stats: IngestStats = {
      party_a: "", party_b: "", out_of_set_senders: [],
      message_count: 0, accepted_count: 0, rejected_count: messageLike.length,
      non_printables_stripped: 0, glyph_artifacts_stripped: 0,
      date_range: { start: null, end: null },
      timezone: null, needs_timezone: true, zone_source: null,
      volume_by_party: {}, attachment_count: 0, reply_count: 0,
      read_receipt_count: 0, event_count: eventCount,
      quote_blocks_folded: counts.quote_blocks,
      unparsed_headers: unparsed.length,
      unknown_column_lines: counts.unknown_column_lines,
      decorative_glyph_lines: counts.decorative_glyph_lines,
      pending_message_count: messageLike.length,
      page_count: pages.length,
    };
    return {
      rows: [], source_metas: [], events: [], stats,
      csv: rowsToCsv([]),
      case_meta: buildCaseMeta(opts.corpusId, stats),
      record_info: info,
      warnings: [
        `This export does not record a time zone, so ${messageLike.length} messages are waiting for one.`,
      ],
    };
  }

  const { party_a, party_b, out_of_set } = determineParties(records);
  let nonPrintables = 0;
  let glyphArtifacts = 0;
  const provisional: { ts: string; row: CanonicalRow; meta: SourceMetadata }[] = [];
  const events: CallEvent[] = [];

  for (const r of records) {
    const ts = toUtcIso(r.dt, tz);
    const s = sanitize(r.sender);
    nonPrintables += s.stripped;

    if (r.kind === "event") {
      events.push({
        timestamp: ts, sender: s.text, predicate: r.predicate,
        source_page: r.source_page,
      });
      continue;
    }

    const b = sanitize(r.body);
    nonPrintables += b.stripped;
    const cleaned = stripGlyphArtifacts(b.text);
    glyphArtifacts += cleaned.count;

    let recipient = "";
    if (r.viewed_by) {
      const v = sanitize(r.viewed_by);
      nonPrintables += v.stripped;
      recipient = v.text;
    } else if (s.text === party_a || s.text === party_b) {
      recipient = s.text === party_a ? party_b : party_a;
    }

    provisional.push({
      ts,
      row: {
        timestamp: ts, sender: s.text, recipient, subject: "",
        body: cleaned.text, attachments: attachmentsSummary(r.attachments),
      },
      meta: {
        source: ADAPTER_NAME, message_number: 0, kind: r.kind,
        predicate: r.predicate,
        viewed_by: recipient || null,
        viewed_at: r.viewed_dt ? toUtcIso(r.viewed_dt, tz) : null,
        attachments_structured: r.attachments,
        reply_to: r.reply_to,
        source_page: r.source_page,
      },
    });
  }

  provisional.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
  const rows = provisional.map((p) => p.row);
  const metas = provisional.map((p, i) => ({ ...p.meta, message_number: i + 1 }));
  events.sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));

  const volume: Record<string, number> = {};
  for (const r of rows) volume[r.sender] = (volume[r.sender] ?? 0) + 1;

  const stats: IngestStats = {
    party_a, party_b, out_of_set_senders: out_of_set,
    message_count: rows.length,
    accepted_count: rows.length,
    rejected_count: 0,
    non_printables_stripped: nonPrintables,
    // Tokens stripped from inside bodies PLUS whole rows that were
    // nothing but a codepoint spelling — pdfplumber sees the first shape,
    // pdf.js the second, so both count toward one number.
    glyph_artifacts_stripped: glyphArtifacts + counts.glyph_artifact_rows,
    date_range: {
      start: rows.length ? rows[0].timestamp : null,
      end: rows.length ? rows[rows.length - 1].timestamp : null,
    },
    timezone: tz,
    needs_timezone: false,
    zone_source: "user_assigned",
    volume_by_party: volume,
    attachment_count: rows.filter((r) => r.attachments).length,
    reply_count: metas.filter((m) => m.reply_to).length,
    read_receipt_count: metas.filter((m) => m.viewed_at).length,
    event_count: events.length,
    quote_blocks_folded: counts.quote_blocks,
    unparsed_headers: unparsed.length,
    unknown_column_lines: counts.unknown_column_lines,
    decorative_glyph_lines: counts.decorative_glyph_lines,
    page_count: pages.length,
  };

  const warnings: string[] = [];
  if (unparsed.length) {
    warnings.push(`${unparsed.length} record header(s) could not be read.`);
  }
  if (counts.unknown_column_lines) {
    warnings.push(
      `${counts.unknown_column_lines} line(s) sat at an unrecognized column.`,
    );
  }

  return {
    rows, source_metas: metas, events, stats,
    csv: rowsToCsv(rows),
    case_meta: buildCaseMeta(opts.corpusId, stats),
    record_info: info,
    warnings,
  };
}

function buildCaseMeta(corpusId: string, stats: IngestStats): CaseMeta {
  const meta: CaseMeta = {
    id: corpusId,
    privacy_class: "private",
    prompt_vars: {
      party_a: stats.party_a,
      party_b: stats.party_b,
      format: "AppClose Records Export PDF, canonicalized to CSV (schema v1)",
      time_period: formatTimePeriod(stats.date_range.start, stats.date_range.end),
      context: "TODO: describe the context (e.g. 'their 9-year-old son Murphy')",
    },
    known_patterns: [],
    notes: "TODO: optional freeform notes about this corpus",
  };
  if (stats.timezone) {
    meta.source_timezone = stats.timezone;
    meta.predicted_timezone = stats.timezone;
    meta.user_timezone = stats.timezone;
  }
  return meta;
}

/** Convenience for callers holding raw pdf.js text content. */
export function pagesFromTextContent(
  contents: { items: unknown[] }[],
): { pageNumber: number; lines: PositionedLine[] }[] {
  return contents.map((tc, i) => ({
    pageNumber: i + 1,
    lines: extractPageLines(tc),
  }));
}
